import React from 'react';
import { Box, Stack, SxProps, TablePagination } from '@mui/material';
import { useTracking } from 'services/tracking/hooks';
import { TrackingEventName } from 'services/tracking/constants';
import { Tabs } from 'components/Tabs';
import { hideUnavailablePageArrows } from 'components/Table/tablePaginationSx';
import { ConversationItem } from '../types/ConversationItem';
import { useConversationDetailsTab } from 'modules/conversations/atoms/tabsAtom';
import {
  ConversationDetailsTabs,
  conversationDetailsTabs
} from 'modules/conversations/constants/conversationDetailsTabs';

const tabsContainer: SxProps = {
  my: 1
};

type Props = {
  moveToConversation: (direction: 'next' | 'previous') => void;
  conversationsCount: number | undefined;
  conversationIndex: number;
  onTabChange?: (tab: ConversationDetailsTabs) => void;
  conversation: ConversationItem;
};

export const ConversationsNavigator: React.FC<Props> = ({
  moveToConversation,
  conversationsCount,
  conversationIndex,
  onTabChange,
  conversation
}) => {
  const { track } = useTracking();
  const { selectedTab, setSelectedTab } = useConversationDetailsTab();
  const calendarBookingId = !!conversation.calendarBookingId;

  const handleTabChange = (value: ConversationDetailsTabs) => {
    setSelectedTab(value);
    if (onTabChange) {
      onTabChange(value);
    }
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      display="flex"
      justifyContent={calendarBookingId ? 'space-between' : 'flex-end'}
      width="100%"
    >
      {calendarBookingId && (
        <Box sx={tabsContainer}>
          <Tabs
            tabs={conversationDetailsTabs}
            value={selectedTab}
            onChange={(value) =>
              handleTabChange(value as ConversationDetailsTabs)
            }
          />
        </Box>
      )}
      <Box>
        {/* An unknown position (-1) would read "0 of N" with arrows that do
            nothing, as after browser Back or a link to a later page. */}
        {!!conversationsCount && conversationIndex >= 0 && (
          <TablePagination
            component="div"
            sx={hideUnavailablePageArrows}
            labelDisplayedRows={({ from, count }) => `${from} of ${count}`}
            count={conversationsCount}
            page={conversationIndex}
            rowsPerPage={1}
            rowsPerPageOptions={[]}
            onPageChange={(event, page) => {
              const isNext = page > conversationIndex;
              track(
                isNext
                  ? TrackingEventName.CampaignDashboardConversationNext
                  : TrackingEventName.CampaignDashboardConversationPrevious
              );
              moveToConversation(isNext ? 'next' : 'previous');
            }}
          />
        )}
      </Box>
    </Stack>
  );
};
